"use client"

import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "@/lib/icons"
import { useTranslations } from "next-intl"
import type React from "react"

// Extracted from components/booru-detail-modal.tsx (pure move: same JSX, same behaviour).
interface AlbumNavBarProps {
  albumNavBottomPx: number
  currentAlbumPage: number
  handleAlbumNextPage: () => void
  handleAlbumPrevPage: () => void
  handleBottomSwipeTouchEnd: (e: React.TouchEvent) => void
  handleBottomSwipeTouchStart: (e: React.TouchEvent) => void
  handleZoomIn: () => void
  handleZoomOut: () => void
  setZoom: React.Dispatch<React.SetStateAction<number>>
  showAlbumNav: boolean
  totalAlbumPages: number
  viewingAdSlot: number | null
  zoom: number
}

export function AlbumNavBar({
  albumNavBottomPx,
  currentAlbumPage,
  handleAlbumNextPage,
  handleAlbumPrevPage,
  handleBottomSwipeTouchEnd,
  handleBottomSwipeTouchStart,
  handleZoomIn,
  handleZoomOut,
  setZoom,
  showAlbumNav,
  totalAlbumPages,
  viewingAdSlot,
  zoom,
}: AlbumNavBarProps) {
  const tt = useTranslations("booruDetailModal")
  return (
    <>
      {showAlbumNav && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-20"
                  style={{ bottom: albumNavBottomPx }}
                  onClick={(e) => e.stopPropagation()}
                  onTouchStart={handleBottomSwipeTouchStart}
                  onTouchEnd={handleBottomSwipeTouchEnd}
                >
                  <div className="w-8 h-1 rounded-full bg-white/30" />
                <div className="flex items-center gap-1 bg-black/60 rounded-full px-1.5 py-1">
                  {/* Prev page button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-white/80 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                    onClick={handleAlbumPrevPage}
                    disabled={viewingAdSlot === null && currentAlbumPage === 0}
                    title={tt("previousPage2")}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>

                  {/* Zoom out */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-white/80 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                    onClick={handleZoomOut}
                    disabled={zoom <= 0.1}
                    title={tt("zoomOut")}
                  >
                    <ZoomOut className="h-3.5 w-3.5" />
                  </Button>

                  {/* Zoom percentage */}
                  <button
                    className="px-1.5 text-[10px] text-white/80 hover:text-white transition-all duration-150 min-w-[2.5rem] text-center active:scale-95"
                    onClick={() => setZoom(1)}
                    title={tt("resetZoom")}
                  >
                    {Math.round(zoom * 100)}%
                  </button>

                  {/* Zoom in */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-white/80 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                    onClick={handleZoomIn}
                    disabled={zoom >= 5}
                    title={tt("zoomIn")}
                  >
                    <ZoomIn className="h-3.5 w-3.5" />
                  </Button>

                  {/* Next page button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-white/80 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                    onClick={handleAlbumNextPage}
                    disabled={viewingAdSlot === null && currentAlbumPage === totalAlbumPages - 1}
                    title={tt("nextPage2")}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
                </div>
              )}
    </>
  )
}
